import { apiGet, type QueryOf, type ResponseOf } from './http';
import { asConsiderationStatus, type ConsiderationStatus } from './vocabularies';

type DashboardFromApi = ResponseOf<'/api/v1/dashboard/', 'get'>;

/**
 * The payload as the screen may treat it: the document's own shape, except for the one field it types
 * as a plain `string` — `PipelineSegment.status` — which is narrowed to its vocabulary below.
 */
export type Dashboard = Omit<DashboardFromApi, 'pipeline' | 'feedback_distribution'> & {
  pipeline?: DashboardPipeline;
  feedback_distribution?: DashboardDistribution;
};

/**
 * The `?period=` values, **read off the schema** rather than restated here.
 *
 * The backend's own OpenAPI document carries the enum on the query parameter
 * (`schema.d.ts`: `period?: "last_30_days" | "last_90_days" | "all_time"`), and `QueryOf` is the
 * helper that reaches it. Hand-writing the union compiles just as well and is exactly how a screen
 * starts believing in a window the API has stopped accepting — the rule in
 * `.claude/rules/frontend.md` is that no API type is written by hand, and `api.test.ts` now fails a
 * type alias in `src/api/` that does not derive from the schema.
 */
export type DashboardPeriod = NonNullable<
  NonNullable<QueryOf<'/api/v1/dashboard/', 'get'>>['period']
>;

/**
 * The four analysis sections, each narrowed out of the generated schema rather than restated.
 *
 * They are optional on the wire — the sections a caller's group may see are decided **in the
 * body** (ADR 0015), so a section the API omitted is a section the screen does not draw. Narrowing
 * with `NonNullable` gives each section component a non-optional prop, which makes "absent means
 * not rendered" a type-level fact instead of a habit.
 */
export type DashboardHealth = NonNullable<DashboardFromApi['health']>;
export type DashboardActivity = NonNullable<DashboardFromApi['interviewer_activity']>;

type PipelineFromApi = NonNullable<DashboardFromApi['pipeline']>;
type DistributionFromApi = NonNullable<DashboardFromApi['feedback_distribution']>;

/**
 * ⚠️ **The one dashboard field the document types as `string`, narrowed here at the edge.**
 *
 * `PipelineSegment.status` is a stored code typed `string` in the OpenAPI document (`api.test.ts`'s
 * enum guard names it), while the values are `ConsiderationStatus`. Section 3 renders it through
 * `StatusDot`: `statusDotStyles[value]` is `undefined` for anything else and reading `style.pill` off
 * it **throws during render**, so one unexpected code does not lose a segment — it loses the whole
 * dashboard, and `RouteError` is what the viewer gets. Sprint 8 wrote `segment.status as
 * ConsiderationStatus`, which is a promise to the compiler rather than a check.
 *
 * ⚠️ **This note named two fields, and the second half is retracted** (BUG-060). It read
 * *"`PipelineSegment.status` and `VerdictCount.verdict` are stored codes typed `string`"*, with
 * Section 4 narrowing the verdict here beside the status. The document has declared that field as the
 * `OverallRecommendation` enum since backend TASK-104, `VerdictCount` below is therefore the
 * document's own shape, and the helper that narrowed it went with BUG-038 — so there is one field left
 * at this edge and not two.
 *
 * **A code outside the vocabulary is dropped from the list**, and that is a real cost stated rather
 * than hidden: the segment's count disappears from the bar, from the visible list, from the figure's
 * accessible summary and from Section 3's total. There is no honest alternative on this screen — a
 * status the client cannot name has no §3 colour, no word to put beside the dot and no `?status=`
 * address to link to, so it cannot be drawn, labelled or filtered on. Dropping it keeps the other four
 * readable; rendering it takes the page down. This is the second line of defence and it is deliberately
 * not the first: typing that field as its enum on the backend is a **Backlog** item (the same one that
 * covered `latest_recommendation` and `overall_recommendation` before TASK-104 typed them), and until
 * it ships this is where an unknown code stops.
 */
export type PipelineSegment = Omit<PipelineFromApi['segments'][number], 'status'> & {
  status: ConsiderationStatus;
};

export type DashboardPipeline = Omit<PipelineFromApi, 'segments'> & {
  segments: PipelineSegment[];
};

/**
 * §6.2 Section 4's buckets — **the document's own shape** since backend TASK-104.
 *
 * ⚠️ It was `Omit<…, 'verdict'> & { verdict: Recommendation }`, and the row was dropped entirely when
 * the value fell outside the four, because a bar with no name is worse than one fewer bar and
 * `StatusDot` threw on an unknown value. The field is the declared enum now — and, unlike every other
 * verdict in the API, it is **not nullable**: a bucket label is always one of the four (all four are
 * always present, a zero is `0`), which the backend asserts in the opposite direction so the two halves
 * of that rule cannot drift into each other.
 */
export type VerdictCount = DistributionFromApi['verdicts'][number];

export type DashboardDistribution = DistributionFromApi;

const narrowPipeline = (pipeline: PipelineFromApi): DashboardPipeline => ({
  ...pipeline,
  segments: pipeline.segments.flatMap((segment) => {
    const status = asConsiderationStatus(segment.status);
    return status ? [{ ...segment, status }] : [];
  }),
});

/**
 * The values the screen may send, in the order §6.2 lists them. A fourth is a **422** from the API
 * — Pydantic refuses it rather than falling back to the default, because answering with numbers for
 * a window nobody asked for is the quiet kind of wrong.
 *
 * The `Record` is what keeps this list derived rather than restated: it must carry a key for every
 * member of `DashboardPeriod` and may carry no other, so a value added to or removed from the
 * backend's enum stops `tsc` here instead of reaching a `<Select>`. `sections.test.tsx` compares the
 * resulting list against the enum in the OpenAPI snapshot, so the runtime values are checked against
 * the document too, not against a literal written beside them.
 */
const PERIODS: Record<DashboardPeriod, true> = {
  last_30_days: true,
  last_90_days: true,
  all_time: true,
};

export const DASHBOARD_PERIODS = Object.keys(PERIODS) as DashboardPeriod[];

/** The default the API itself documents, sent explicitly so the request and the cache key agree. */
export const DEFAULT_DASHBOARD_PERIOD: DashboardPeriod = 'last_30_days';

/**
 * One route, one payload, and **the sections a caller may see are decided in the body** — never by
 * a status code (ADR 0015). An Interviewer's response simply omits `health`, `pipeline` and
 * `interviewer_activity`; the screen renders what it is given and asks for nothing else.
 *
 * The response is viewer-dependent, so every cache entry is keyed on the viewer
 * (`queryKeys.dashboard`).
 */
export async function fetchDashboard(period?: DashboardPeriod): Promise<Dashboard> {
  const {
    pipeline,
    feedback_distribution: distribution,
    ...body
  } = await apiGet('/api/v1/dashboard/', { query: period ? { period } : undefined });

  /*
   * ⚠️ **An absent section stays absent from the object** — a conditional spread, not
   * `pipeline: pipeline ? … : undefined`. An Interviewer's body has no `pipeline` key at all and the
   * screen renders what it is given (`{pipeline && <PipelineSection …>}`); writing the key in as
   * `undefined` would work today and would be the first step towards a section drawn as an empty
   * frame, which §6.2 rules out. `exclude_unset=True` on the route is what makes the omission
   * meaningful in the first place.
   */
  return {
    ...body,
    ...(pipeline ? { pipeline: narrowPipeline(pipeline) } : {}),
    // Section 4 is handed on as sent: its bucket labels are the API's declared enum since TASK-104,
    // so there is no row left to drop and no shape to rebuild. Section 3 still narrows, because
    // `PipelineSegment.status` is a plain string in the document — a different vocabulary, its own
    // Backlog line, and deliberately not this task's.
    ...(distribution ? { feedback_distribution: distribution } : {}),
  };
}
